import { useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, Flame, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LangSwitch } from "@/components/LangSwitch";
import { useAdminLang, useAuth } from "./context";

export default function Login() {
  const { t, lang, setLang } = useAdminLang();
  const { user, login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to={loc.state?.from || "/admin"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login(email.trim(), password);
      nav(loc.state?.from || "/admin", { replace: true });
    } catch (err) {
      setError(err.response?.status === 429 ? "login.locked" : "login.error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative grid min-h-screen place-items-center overflow-hidden p-4">
      <div aria-hidden className="pointer-events-none absolute -top-40 start-1/2 h-[480px] w-[480px] -translate-x-1/2 rounded-full bg-primary/20 blur-[120px] rtl:translate-x-1/2" />
      <div className="absolute end-4 top-4"><LangSwitch lang={lang} onChange={setLang} testid="admin-lang-switch" /></div>
      <form onSubmit={submit} data-testid="login-form" className="relative w-full max-w-sm space-y-6 rounded-3xl border bg-card/80 p-8 shadow-2xl backdrop-blur-xl">
        <div className="space-y-3">
          <span className="grid h-12 w-12 place-items-center rounded-2xl bg-primary"><Flame className="h-6 w-6 text-white" /></span>
          <h1 className="text-2xl font-extrabold tracking-tight">{t("login.title")}</h1>
          <p className="text-sm text-muted-foreground">{t("login.subtitle")}</p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="email">{t("login.email")}</Label>
          <Input id="email" type="email" dir="ltr" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email-input" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password">{t("login.password")}</Label>
          <Input id="password" type="password" dir="ltr" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input" />
        </div>
        {error && <p className="rounded-lg bg-destructive/15 px-3 py-2 text-sm text-red-300" data-testid="login-error">{t(error)}</p>}
        <Button type="submit" className="h-11 w-full rounded-full font-semibold" disabled={busy} data-testid="login-submit-btn">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}
          {busy ? t("login.signingIn") : t("login.submit")}
        </Button>
        <Link to="/" className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground hover:text-foreground" data-testid="login-back-link">
          <ArrowLeft className="h-3.5 w-3.5 rtl:rotate-180" />
          {t("login.back")}
        </Link>
      </form>
    </div>
  );
}
